import { t } from "@lingui/macro";
import { CheckCircleIcon } from "@phosphor-icons/react";
import type { Template } from "@reactive-resume/utils";
import { cn, templatesList } from "@reactive-resume/utils";

import { templateCardUrl, templateImageUrl } from "@/client/constants/template-images";
import { applyTemplateThemeToData } from "@/client/constants/template-presets";
import { useResumeStore } from "@/client/stores/resume";

export const TemplatesStep = () => {
  const metadata = useResumeStore((state) => state.resume.data.metadata);

  const applyTemplate = (template: Template) => {
    const data = useResumeStore.getState().resume.data;
    const updated = applyTemplateThemeToData(data, template);
    useResumeStore.getState().setData(updated);
  };

  return (
    <section className="mx-auto w-full max-w-[920px] pb-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">
        Personnalisation
      </p>

      <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-indigo-950 sm:text-[28px]">
        Choisissez le style de votre CV
      </h1>

      <p className="mt-1.5 text-xs leading-5 text-slate-500">
        Changez de modèle ou de couleur sans perdre les informations déjà saisies.
      </p>

      <p className="mt-4 rounded-xl border border-indigo-100 bg-white px-4 py-3 text-xs leading-5 text-slate-600">
        {t`Retrouvez les couleurs, la police et la taille du texte dans la barre sous l’aperçu du CV.`}
      </p>

      <div className="mt-6 flex items-center justify-between">
        <h2 className="text-sm font-bold text-indigo-950">Modèles de CV</h2>

        <span className="text-[10px] font-semibold text-slate-400">
          {templatesList.length} modèles disponibles
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {templatesList.map((template) => {
          const active = metadata.template === template;

          return (
            <article
              key={template}
              className={cn(
                "overflow-hidden rounded-xl border-2 bg-white",
                active ? "border-indigo-600" : "border-slate-200",
              )}
            >
              <div className="relative aspect-[1/1.1] overflow-hidden bg-slate-100 p-2">
                <img
                  src={templateCardUrl(template)}
                  alt={`Modèle ${template}`}
                  loading="lazy"
                  decoding="async"
                  onError={(event) => {
                    const image = event.currentTarget;
                    const reference = templateImageUrl(template);
                    if (!image.src.endsWith(reference)) image.src = reference;
                  }}
                  className="h-full w-full object-contain object-top"
                />

                {active && (
                  <span className="absolute inset-0 grid place-items-center bg-white/25">
                    <CheckCircleIcon
                      size={46}
                      weight="fill"
                      className="rounded-full bg-white text-emerald-500"
                    />
                  </span>
                )}
              </div>

              <div className="p-2.5">
                <h3 className="truncate text-xs font-bold capitalize text-indigo-950">
                  {template}
                </h3>

                <button
                  type="button"
                  onClick={() => {
                    applyTemplate(template);
                  }}
                  className={cn(
                    "mt-2 h-9 w-full rounded-lg text-[10px] font-bold",
                    active
                      ? "bg-indigo-600 text-white"
                      : "border border-indigo-500 text-indigo-600 hover:bg-indigo-50",
                  )}
                >
                  {active ? "Modèle actuel" : "Utiliser ce modèle"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};
